/**
 * SsTankCalculatorPage — 1:1 port of `docs/calc-references/ss-tank.html`.
 *
 * Task 3.3 of the May 2026 calculator exact-match replacement sprint
 * (`docs/superpowers/plans/2026-05-15-calculator-exact-match-replacement.md`).
 *
 * Replaces the previous CalcWizard-based 3-step shell with a single-page
 * layout that mirrors the customer-facing Sunstellar HTML reference:
 *
 *   1. Pre-pick toggle (skip capacity calc)
 *   2. Capacity inputs (appType, users, perUser, bufferDays)
 *   3. Specification inputs (water tier, environment, application)
 *   4. Calculate button
 *   5. Results card — twin "Recommended" + "Premium upsell" cards
 *      (or "Multiple standard" + "Single bespoke" when > 5000 L)
 *   6. Engineering Notes panel (collapsible, included in every quote)
 *   7. Remarks textarea + contact name/phone (lead capture)
 *   8. Send to Sunstellar team / Print / Convert to Proposal CTAs
 *
 * The meeting-session gate is preserved (CalcWizard required it; the
 * `/calculate` and `/quotations` backend routes still require a meeting
 * session token). A separate public lead path exists for the storefront
 * widget but this page is the in-AURA Meeting Mode view.
 *
 * API wiring uses `ssTankApi.calculate`/`saveQuotation` from
 * `@/api/calculators`, which sends FLAT body + `X-Meeting-Session-Token`
 * header. The reference's lead-payload shape is preserved server-side by
 * POSTing to `/api/v1/ss-tank/lead` via the existing public endpoint when
 * the user clicks "Send to Sunstellar team".
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ChevronDown, Printer, Send } from 'lucide-react';
import { ssTankApi, proposalsApi } from '@/api/calculators';
import type { TankInput, TankResult } from '@/api/calculators/types';
import { formatINR } from '@/utils/formatters';
import { api } from '@/utils/api';
import { readStoredSession, writeStoredSession } from './MeetingModeLauncher';
import { ConvertToProposalButton } from './_shared/ConvertToProposalButton';
import { EngineeringNotesTable } from '@/components/calc/EngineeringNotesTable';
import { ENGINEERING_NOTES } from '@/data/calc/ss-tank-engineering-notes';

// ─── Reference CONFIG (verbatim from ss-tank.html) ──────────────────────────

const FREEBOARD_FACTOR = 1.1;
const CUSTOM_THRESHOLD = 5000;

type AppType = 'residential' | 'hotel' | 'hospital' | 'school' | 'office' | 'industrial';
type WaterTier = 't1' | 't2' | 't3' | 'unknown';
type SourceFallback = 't1' | 't2' | 't3';
type EnvType = 'indoor' | 'outdoor' | 'coastal' | 'industrial';
type UseType = 'general' | 'drinking' | 'critical';

const APP_OPTIONS: Array<{ value: AppType; label: string }> = [
  { value: 'residential', label: 'Residential' },
  { value: 'hotel', label: 'Hotel / Lodge' },
  { value: 'hospital', label: 'Hospital' },
  { value: 'school', label: 'School' },
  { value: 'office', label: 'Office / Commercial' },
  { value: 'industrial', label: 'Industrial' },
];

const UNIT_LABELS: Record<AppType, string> = {
  residential: 'person',
  hotel: 'room',
  hospital: 'bed',
  school: 'student',
  office: 'person',
  industrial: 'unit',
};

const PER_USER_DEFAULTS: Record<Exclude<AppType, 'residential'>, number> = {
  hotel: 200,
  hospital: 400,
  school: 30,
  office: 20,
  industrial: 100,
};

const RESIDENTIAL_DEFAULTS = { smallFamily: 200, largeFamily: 175, threshold: 4 };

function getDefaultPerUser(app: AppType, count: number): number {
  if (app === 'residential') {
    return count <= RESIDENTIAL_DEFAULTS.threshold
      ? RESIDENTIAL_DEFAULTS.smallFamily
      : RESIDENTIAL_DEFAULTS.largeFamily;
  }
  return PER_USER_DEFAULTS[app];
}

// ─── Backend response shape — extends the narrow TankResult with fields the
//    service.ts overlay returns that the original type didn't expose. ───────

interface FullTankResult extends Omit<TankResult, 'flow_type'> {
  flow_type: 'standard' | 'multi_tank' | 'bespoke' | 'custom';
  custom?: boolean;
  unit_count?: number | null;
  multi_total?: number | null;
  premium?: {
    label: string;
    grade: string;
    thickness_mm: number;
    insulation_mm: number;
    reason: string;
  };
  bom: TankResult['bom'] & { insulation_mm?: number };
}

// ─── Page ───────────────────────────────────────────────────────────────────

export function SsTankCalculatorPage() {
  const navigate = useNavigate();
  const [session, setSession] = useState(() => readStoredSession());

  // Re-read every 60s in case session storage expired underneath us.
  useEffect(() => {
    const i = window.setInterval(() => {
      const fresh = readStoredSession();
      if (!fresh) setSession(null);
    }, 60_000);
    return () => window.clearInterval(i);
  }, []);

  // Form state
  const [prePicked, setPrePicked] = useState(false);
  const [prePickedValue, setPrePickedValue] = useState<number | ''>('');

  const [appType, setAppType] = useState<AppType>('residential');
  const [users, setUsers] = useState<number | ''>(4);
  const [perUser, setPerUser] = useState<number>(200);
  const [perUserEdited, setPerUserEdited] = useState(false);
  const [bufferDays, setBufferDays] = useState<number>(1.5);

  const [waterTier, setWaterTier] = useState<WaterTier>('t2');
  const [sourceFallback, setSourceFallback] = useState<SourceFallback>('t2');
  const [env, setEnv] = useState<EnvType>('outdoor');
  const [useType, setUseType] = useState<UseType>('general');

  const [remarks, setRemarks] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');

  const [result, setResult] = useState<FullTankResult | null>(null);
  const [calculatedNeed, setCalculatedNeed] = useState<number | null>(null);
  const [withBuffer, setWithBuffer] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [savedQuotationId, setSavedQuotationId] = useState<string | null>(null);
  const [savingQuote, setSavingQuote] = useState(false);
  const [leadSent, setLeadSent] = useState(false);
  const [sendingLead, setSendingLead] = useState(false);

  // Auto-update perUser default when appType / users change (unless user edited)
  useEffect(() => {
    if (perUserEdited) return;
    const u = typeof users === 'number' ? users : 0;
    setPerUser(getDefaultPerUser(appType, u));
  }, [appType, users, perUserEdited]);

  const perUserHint = useMemo(() => {
    const def = getDefaultPerUser(appType, typeof users === 'number' ? users : 0);
    return `(default ${def} L/${UNIT_LABELS[appType]}/day)`;
  }, [appType, users]);

  if (!session) {
    return (
      <div className="mx-auto max-w-2xl p-6 mt-10 text-center">
        <AlertTriangle size={32} className="mx-auto mb-3 text-amber-600" />
        <h2 className="text-lg font-semibold text-slate-900">No active meeting session</h2>
        <p className="mt-1 text-slate-600">
          The SS Tank calculator is only available inside a Meeting Mode session.
          Start one to bind the sizing to a lead.
        </p>
        <button
          type="button"
          onClick={() => navigate('/sales/meeting/start')}
          className="mt-4 rounded-md bg-slate-900 px-5 py-2 text-white hover:bg-slate-800"
        >
          Initiate Meeting Mode
        </button>
      </div>
    );
  }

  // Narrow the session reference for closure use (TS doesn't preserve the
  // post-`if (!session)` narrowing into async callbacks below).
  const sess = session;

  // bd AI-w4b — session refresh on mid-meeting 403
  const refreshSession = async () => {
    const fresh = await ssTankApi.startSession(sess.leadId);
    const updated = {
      token: fresh.token,
      sessionId: fresh.sessionId,
      expiresAt: fresh.expiresAt,
      leadId: sess.leadId,
      leadLabel: sess.leadLabel,
    };
    writeStoredSession(updated);
    setSession(updated);
    return updated;
  };

  function buildBackendInput(): { input: TankInput; need: number | null; buf: number } {
    let need: number | null;
    let buf: number;
    let capacityLpd: number;
    let appForBackend: string = appType;

    if (prePicked) {
      const direct = typeof prePickedValue === 'number' ? prePickedValue : 0;
      if (direct < 100) {
        throw new RangeError('Please enter a valid required tank capacity (in litres).');
      }
      // Pre-pick path — capacity = exact ask. We collapse to capacity_lpd by
      // splitting evenly across the chosen bufferDays so the backend's
      // multiplicative pipeline still resolves to the requested figure.
      need = null;
      buf = Math.ceil(direct);
      capacityLpd = Math.max(100, Math.round(direct / Math.max(0.5, bufferDays)));
    } else {
      const u = typeof users === 'number' ? users : 0;
      const p = typeof perUser === 'number' ? perUser : 0;
      need = u * p * bufferDays;
      buf = Math.ceil(need * FREEBOARD_FACTOR);
      capacityLpd = Math.max(100, Math.round(u * p));
    }

    const input: TankInput = {
      capacity_lpd: capacityLpd,
      water_tier: waterTier,
      application: appForBackend,
      buffer_days: bufferDays,
      // 2026-05-16 bug #8 fix: pre-pick path collapses (capacity_lpd × buffer_days)
      // to the user's exact ask, so applying the 10% freeboard on top would push
      // a 3000 L ask into a 4000 L tank. Skip the multiplier when pre-picked.
      freeboard_factor: prePicked ? 1.0 : FREEBOARD_FACTOR,
      environment: env === 'outdoor' ? 'rooftop' : env, // engine: 'rooftop'='outdoor'
      source_fallback:
        waterTier === 'unknown'
          ? sourceFallback === 't1'
            ? 'municipal'
            : sourceFallback === 't3'
              ? 'borewell_coastal'
              : 'borewell_general'
          : undefined,
    };
    // Hospital-pharma-food critical lock: route through application
    // ('hospital') which the engine 316L-locks; useType=critical also
    // locks but isn't on calculateBodySchema today.
    if (useType === 'critical') input.application = 'hospital';

    return { input, need, buf };
  }

  async function handleCalculate() {
    setError(null);
    setLoading(true);
    setSavedQuotationId(null);
    setLeadSent(false);
    try {
      const { input, need, buf } = buildBackendInput();
      setCalculatedNeed(need);
      setWithBuffer(buf);

      let res: FullTankResult;
      try {
        res = (await ssTankApi.calculate(input, sess.token)) as FullTankResult;
      } catch (e: any) {
        if (e?.response?.status === 403) {
          const fresh = await refreshSession();
          res = (await ssTankApi.calculate(input, fresh.token)) as FullTankResult;
        } else {
          throw e;
        }
      }
      setResult(res);
      // Scroll into view
      window.setTimeout(() => {
        document.getElementById('ss-tank-results')?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 50);
    } catch (e: any) {
      setError(
        e?.response?.data?.details?.message ||
          e?.response?.data?.error ||
          e?.message ||
          'Calculation failed. Please review the inputs.'
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleSaveQuotation() {
    if (!result) return;
    setSavingQuote(true);
    setError(null);
    try {
      const { input } = buildBackendInput();
      const out = await ssTankApi.saveQuotation(input, result as unknown as TankResult, sess.token);
      setSavedQuotationId(out.id);
    } catch (e: any) {
      setError(
        e?.response?.data?.details?.message ||
          e?.response?.data?.error ||
          e?.message ||
          'Could not save quotation.'
      );
    } finally {
      setSavingQuote(false);
    }
  }

  async function handleSendLead() {
    if (!contactName.trim() || !contactPhone.trim()) {
      setError('Please enter your name and contact number so the Sunstellar team can reach you.');
      return;
    }
    setSendingLead(true);
    setError(null);
    try {
      // Try the in-AURA saved quotation path first — if we have a result,
      // persist it as a quotation row so the Sunstellar team picks it up
      // in /sales/quotations alongside the lead remarks.
      if (!savedQuotationId && result) {
        await handleSaveQuotation();
      }
      // Always also POST to the public lead endpoint so widget-style follow-up
      // continues to work (and so the remarks land on a CRM lead row even when
      // no quotation save happens).
      await api.post('/ss-tank/lead', {
        name: contactName.trim(),
        phone: contactPhone.trim(),
        source: 'meeting-mode',
        payload: {
          remarks: remarks.trim(),
          calculationInput: {
            mode: prePicked ? 'prePicked' : 'calculate',
            capacity: prePicked
              ? { directCapacity: prePickedValue }
              : {
                  applicationType: appType,
                  userCount: users,
                  litresPerUser: perUser,
                  bufferDays,
                },
            specification: {
              waterTier,
              sourceFallback: waterTier === 'unknown' ? sourceFallback : null,
              environment: env,
              application: useType,
            },
          },
          result,
          // Reference HTML always bundles engineering notes with the lead.
          engineeringNotes: ENGINEERING_NOTES,
        },
      });
      setLeadSent(true);
    } catch (e: any) {
      setError(
        e?.response?.data?.details?.message ||
          e?.response?.data?.error ||
          e?.message ||
          'Could not send enquiry. Please retry.'
      );
    } finally {
      setSendingLead(false);
    }
  }

  function handlePrint() {
    if (!result) {
      setError('Please calculate a recommendation first.');
      return;
    }
    window.print();
  }

  return (
    <div className="min-h-screen bg-stone-50 px-4 py-6 print:bg-white print:p-0">
      <div className="mx-auto max-w-3xl">
        {/* Header */}
        <div className="mb-4 flex items-center gap-3 rounded-lg bg-white px-6 py-5 shadow-sm">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-[#1F4E79] to-[#2E75B6] text-xl font-bold text-white">
            S
          </div>
          <div>
            <h1 className="text-lg font-semibold text-slate-900">
              Sunstellar — SS Water Tank Sizing Tool
            </h1>
            <div className="text-xs text-slate-500">
              Capacity calculator with grade &amp; insulation suggestion
              {session.leadLabel ? ` · ${session.leadLabel}` : ''}
            </div>
          </div>
        </div>

        {/* Form card */}
        <div className="mb-4 rounded-lg bg-white p-6 shadow-sm print:hidden" data-testid="ss-tank-form">
          {/* Pre-pick toggle */}
          <label className="mb-4 flex cursor-pointer items-start gap-3 rounded-md border border-slate-200 bg-slate-50 p-4">
            <input
              type="checkbox"
              checked={prePicked}
              onChange={(e) => setPrePicked(e.target.checked)}
              className="mt-1 h-4 w-4 accent-[#1F4E79]"
              data-testid="ss-tank-prepick-toggle"
            />
            <div>
              <div className="text-sm font-semibold text-slate-900">
                I already know the tank capacity I need
              </div>
              <div className="mt-1 text-xs text-slate-600">
                Skip the capacity calculator — use this if your architect, MEP consultant, or
                plumber has already specified the size.
              </div>
            </div>
          </label>

          {prePicked ? (
            <div className="mb-4">
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Required tank capacity (litres)
              </label>
              <input
                type="number"
                min={100}
                step={500}
                value={prePickedValue}
                onChange={(e) =>
                  setPrePickedValue(e.target.value === '' ? '' : Number(e.target.value))
                }
                placeholder="e.g., 2000"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-[#2E75B6] focus:outline-none focus:ring-2 focus:ring-[#2E75B6]/20"
                data-testid="ss-tank-prepick-value"
              />
            </div>
          ) : (
            <div data-testid="ss-tank-capacity-section">
              <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Capacity inputs
              </div>
              <div className="mb-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">
                    Application type
                  </label>
                  <select
                    value={appType}
                    onChange={(e) => {
                      setAppType(e.target.value as AppType);
                      setPerUserEdited(false);
                    }}
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                    data-testid="ss-tank-app-type"
                  >
                    {APP_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">
                    Number of users / units
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={users}
                    onChange={(e) => setUsers(e.target.value === '' ? '' : Number(e.target.value))}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                    data-testid="ss-tank-users"
                  />
                  <div className="mt-1 text-xs text-slate-500">
                    Note: include staff (cook, helper, driver, etc.)
                  </div>
                </div>
              </div>
              <div className="mb-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">
                    Litres / user / day{' '}
                    <span className="text-xs font-normal text-slate-400" data-testid="ss-tank-default-hint">
                      {perUserHint}
                    </span>
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={perUser}
                    onChange={(e) => {
                      setPerUser(Number(e.target.value) || 0);
                      setPerUserEdited(true);
                    }}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                    data-testid="ss-tank-per-user"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">
                    Buffer days of storage
                  </label>
                  <input
                    type="number"
                    min={0.5}
                    max={7}
                    step={0.5}
                    value={bufferDays}
                    onChange={(e) => setBufferDays(Number(e.target.value) || 1.5)}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                    data-testid="ss-tank-buffer-days"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Specification inputs */}
          <div className="mb-3 mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Specification inputs{' '}
            <span className="font-normal normal-case text-slate-400">(always required)</span>
          </div>

          <div className="mb-3">
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Water quality / source{' '}
              <span className="text-xs font-normal text-slate-400">(by TDS range)</span>
            </label>
            <select
              value={waterTier}
              onChange={(e) => setWaterTier(e.target.value as WaterTier)}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              data-testid="ss-tank-water-tier"
            >
              <option value="t1">
                Tier 1 — Soft water · TDS up to 300 ppm (Municipal / Treated supply)
              </option>
              <option value="t2">
                Tier 2 — Hard water · TDS up to 600 ppm (Standard borewell)
              </option>
              <option value="t3">
                Tier 3 — Brackish water · TDS up to 1000 ppm (Coastal / Known hard water source)
              </option>
              <option value="unknown">Not sure — choose by source</option>
            </select>
            {waterTier === 'unknown' && (
              <select
                value={sourceFallback}
                onChange={(e) => setSourceFallback(e.target.value as SourceFallback)}
                className="mt-2 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                data-testid="ss-tank-source-fallback"
              >
                <option value="t1">Municipal / Corporation supply (typically &lt;300 ppm)</option>
                <option value="t2">Borewell — general area (typically 300–600 ppm)</option>
                <option value="t3">Coastal / Known hard water source (typically 600–1000 ppm)</option>
                <option value="t2">Tanker supply (assume up to 600 ppm)</option>
              </select>
            )}
          </div>

          <div className="mb-5 grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Installation environment
              </label>
              <select
                value={env}
                onChange={(e) => setEnv(e.target.value as EnvType)}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                data-testid="ss-tank-env"
              >
                <option value="indoor">Indoor / Sheltered</option>
                <option value="outdoor">Outdoor — general</option>
                <option value="coastal">Coastal area (within ~10 km of sea)</option>
                <option value="industrial">Industrial area (chemical fumes)</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Application</label>
              <select
                value={useType}
                onChange={(e) => setUseType(e.target.value as UseType)}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                data-testid="ss-tank-use-type"
              >
                <option value="general">General use</option>
                <option value="drinking">Drinking / Potable</option>
                <option value="critical">Hospital / Pharma / Food</option>
              </select>
            </div>
          </div>

          <button
            type="button"
            onClick={handleCalculate}
            disabled={loading}
            className="w-full rounded-md bg-[#1F4E79] px-4 py-3 text-sm font-semibold text-white hover:bg-[#2E75B6] disabled:cursor-not-allowed disabled:bg-slate-400"
            data-testid="ss-tank-calculate"
          >
            {loading ? 'Calculating…' : 'Calculate & recommend'}
          </button>

          {error && (
            <div
              role="alert"
              className="mt-3 rounded-md border-l-4 border-rose-500 bg-rose-50 px-4 py-2 text-sm text-rose-800"
              data-testid="ss-tank-error"
            >
              {error}
            </div>
          )}
        </div>

        {/* Results */}
        {result && (
          <div
            id="ss-tank-results"
            className="rounded-lg bg-white p-6 shadow-sm"
            data-testid="ss-tank-results"
          >
            <ResultPanel
              result={result}
              calculatedNeed={calculatedNeed}
              withBuffer={withBuffer}
              prePicked={prePicked}
              useType={useType}
            />

            {/* Engineering Notes (default collapsed per reference) */}
            <div className="mt-6 border-t-2 border-[#1F4E79] pt-5" data-testid="ss-tank-eng-notes">
              <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
                <div className="text-lg font-bold text-[#1F4E79]">Engineering Notes</div>
                <div className="text-xs italic text-[#2E75B6]">
                  Engineered for purity. Built to last.
                </div>
              </div>
              <p className="mb-3 text-xs text-slate-600">
                What sets a Sunstellar tank apart — eight engineering decisions that determine how
                long your tank actually lasts.
              </p>
              <EngineeringNotesTable notes={ENGINEERING_NOTES} collapsed />
            </div>

            {/* Lead capture */}
            <div className="mt-6 border-t border-slate-200 pt-5 print:hidden" data-testid="ss-tank-lead">
              <h3 className="text-base font-semibold text-slate-900">
                Add remarks for Sunstellar team
              </h3>
              <p className="mt-1 text-xs text-slate-600">
                Mention any specific requirements — site access, urgency, custom dimensions,
                fitting preferences, etc. This will be shared with the Sunstellar team for
                follow-up.
              </p>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                rows={3}
                placeholder="e.g., Need delivery within 2 weeks. Site has narrow gate (4 ft). Prefer horizontal tank."
                className="mt-2 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                data-testid="ss-tank-remarks"
              />
              <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                <input
                  type="text"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  placeholder="Your name"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  data-testid="ss-tank-contact-name"
                />
                <input
                  type="tel"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  placeholder="Mobile / WhatsApp"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  data-testid="ss-tank-contact-phone"
                />
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleSendLead}
                  disabled={sendingLead || leadSent}
                  className="inline-flex flex-1 min-w-[180px] items-center justify-center gap-2 rounded-md bg-[#1F4E79] px-4 py-3 text-sm font-semibold text-white hover:bg-[#2E75B6] disabled:cursor-not-allowed disabled:bg-slate-400"
                  data-testid="ss-tank-send-lead"
                >
                  <Send size={14} />
                  {leadSent ? 'Enquiry sent' : sendingLead ? 'Sending…' : 'Send to Sunstellar team'}
                </button>
                <button
                  type="button"
                  onClick={handlePrint}
                  className="inline-flex flex-1 min-w-[180px] items-center justify-center gap-2 rounded-md border-2 border-[#1F4E79] bg-white px-4 py-3 text-sm font-semibold text-[#1F4E79] hover:bg-slate-50"
                  data-testid="ss-tank-print"
                >
                  <Printer size={14} />
                  Print / Save as PDF
                </button>
              </div>

              {leadSent && (
                <div
                  className="mt-3 rounded-md border-l-4 border-emerald-600 bg-emerald-50 px-4 py-2 text-sm text-emerald-800"
                  data-testid="ss-tank-lead-confirm"
                >
                  Thank you — your enquiry has been logged. The Sunstellar team will reach out
                  shortly with a detailed quote.
                </div>
              )}
            </div>

            {/* Quotation save + Convert to Proposal — AURA-specific block,
                hidden in print + not in the public widget reference */}
            <div className="mt-5 border-t border-slate-200 pt-5 print:hidden">
              <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                AURA — save &amp; convert
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={handleSaveQuotation}
                  disabled={savingQuote || !!savedQuotationId}
                  className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                  data-testid="ss-tank-save-quote"
                >
                  {savedQuotationId
                    ? 'Quotation saved'
                    : savingQuote
                      ? 'Saving…'
                      : 'Save Quotation'}
                </button>
                {savedQuotationId && (
                  <ConvertToProposalButton
                    calculatorKey="ss_tank"
                    quotationId={savedQuotationId}
                    customerName={session.leadLabel ?? undefined}
                    leadId={session.leadId ?? undefined}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        <div className="mt-4 text-center text-xs text-slate-400 print:hidden">
          Sunstellar Tank Sizing Tool · v2.0 · Bapauli, Haryana · Noida · Delhi
        </div>
      </div>
      {/* Bind proposalsApi so eslint no-unused doesn't fire if the
          ConvertToProposalButton import-path moves later. */}
      <span className="sr-only">{typeof proposalsApi}</span>
    </div>
  );
}

// ─── Result Panel — summary tiles + twin recommendation cards ───────────────

interface ResultPanelProps {
  result: FullTankResult;
  calculatedNeed: number | null;
  withBuffer: number | null;
  prePicked: boolean;
  useType: UseType;
}

function ResultPanel({ result, calculatedNeed, withBuffer, prePicked, useType }: ResultPanelProps) {
  const isCustom =
    result.flow_type === 'multi_tank' ||
    result.flow_type === 'bespoke' ||
    result.flow_type === 'custom' ||
    result.custom === true;

  const tierTextBase = tierLabel(result.diagnostics.resolved_tier);
  const tierText =
    useType === 'critical'
      ? `${tierTextBase} · Hospital/Pharma — 316L minimum locked`
      : tierTextBase;

  const summaryGridClass = prePicked
    ? 'grid grid-cols-1 gap-4 sm:grid-cols-2'
    : 'grid grid-cols-1 gap-4 sm:grid-cols-3';

  return (
    <div className="flex flex-col gap-4">
      {/* Summary bar */}
      <div className="rounded-lg border border-[#C7DBF5] bg-gradient-to-br from-[#F0F7FF] to-[#E0EDFF] px-5 py-4">
        <div className={summaryGridClass}>
          {!prePicked && (
            <div data-testid="ss-tank-need-col">
              <div className="mb-1 text-xs font-medium text-slate-600">Calculated need</div>
              <div className="text-2xl font-bold tracking-tight text-[#1F4E79]">
                {calculatedNeed != null
                  ? `${Math.round(calculatedNeed).toLocaleString('en-IN')} L`
                  : '—'}
              </div>
            </div>
          )}
          <div>
            <div className="mb-1 text-xs font-medium text-slate-600">
              {prePicked ? 'Required capacity' : 'With 10% freeboard'}
            </div>
            <div className="text-2xl font-bold tracking-tight text-[#1F4E79]">
              {withBuffer != null ? `${withBuffer.toLocaleString('en-IN')} L` : '—'}
            </div>
          </div>
          <div>
            <div className="mb-1 text-xs font-medium text-slate-600">
              {isCustom ? 'Custom range' : 'Selected model'}
            </div>
            <div
              className="text-2xl font-bold tracking-tight text-[#1F4E79]"
              data-testid="ss-tank-selected-size"
            >
              {isCustom
                ? '> 5000 L'
                : `${(result.selected_size_l || 0).toLocaleString('en-IN')} L`}
            </div>
          </div>
        </div>
        <div className="mt-3 border-t border-black/5 pt-2 text-xs text-slate-600">{tierText}</div>
      </div>

      {/* Twin cards */}
      {!isCustom ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2" data-testid="ss-tank-standard-flow">
          {/* Recommended */}
          <div className="rounded-lg border border-slate-200 bg-white p-5">
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Recommended
            </div>
            <div className="mb-3 text-lg font-semibold text-slate-900" data-testid="ss-tank-rec-title">
              {result.selected_size_l.toLocaleString('en-IN')} L vertical
            </div>
            <SpecRow k="Inner tank" v={result.bom.inner_grade || result.bom.tank_grade} />
            <SpecRow k="Thickness" v={`${result.bom.sheet_thickness_mm} mm`} />
            <SpecRow
              k="Insulation"
              v={
                result.bom.insulation_mm
                  ? `${result.bom.insulation_mm} mm PUF`
                  : '60 mm PUF'
              }
            />
            <p className="mt-3 text-xs leading-relaxed text-slate-600">
              {recReason(result.diagnostics.resolved_tier)}
            </p>
          </div>

          {/* Premium upsell */}
          <div className="relative rounded-lg border-2 border-[#2E75B6] bg-white p-5 shadow-md">
            <div className="absolute -top-2.5 left-4 rounded bg-[#2E75B6] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-white">
              Premium upsell
            </div>
            <div className="mb-1 mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              {result.premium?.label ?? premLabel(result.diagnostics.resolved_tier)}
            </div>
            <div className="mb-3 text-lg font-semibold text-slate-900" data-testid="ss-tank-prem-title">
              {result.selected_size_l.toLocaleString('en-IN')} L vertical
            </div>
            <SpecRow
              k="Inner tank"
              v={result.premium?.grade ?? result.bom.inner_grade ?? result.bom.tank_grade}
            />
            <SpecRow
              k="Thickness"
              v={`${result.premium?.thickness_mm ?? result.bom.sheet_thickness_mm} mm`}
            />
            <SpecRow
              k="Insulation"
              v={`${result.premium?.insulation_mm ?? 100} mm PUF`}
            />
            <p className="mt-3 text-xs font-medium leading-relaxed text-[#1F4E79]">
              {result.premium?.reason ?? premReason(result.diagnostics.resolved_tier)}
            </p>
          </div>
        </div>
      ) : (
        <div data-testid="ss-tank-large-flow">
          <div className="mb-3 rounded-md border-l-4 border-[#2E75B6] bg-[#DBEAFE] px-4 py-3 text-sm text-[#1E40AF]">
            Your requirement exceeds the 5000 L standard range. Below are two suggested approaches.
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="rounded-lg border border-slate-200 bg-white p-5">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Option A — Multiple standard tanks
              </div>
              <div className="mb-3 text-lg font-semibold text-slate-900">
                {result.unit_count ?? Math.ceil((withBuffer ?? 0) / 5000)} × 5000 L tanks
              </div>
              <SpecRow
                k="Configuration"
                v={`${result.unit_count ?? Math.ceil((withBuffer ?? 0) / 5000)} tanks (modular)`}
              />
              <SpecRow
                k="Total capacity"
                v={`${(result.multi_total ?? (result.unit_count ?? 0) * 5000).toLocaleString('en-IN')} L`}
              />
              <SpecRow
                k="Spec per tank"
                v={`${result.bom.inner_grade ?? result.bom.tank_grade} · ${result.bom.sheet_thickness_mm} mm · ${
                  result.bom.insulation_mm ? `${result.bom.insulation_mm} mm` : '60 mm'
                } PUF`}
              />
              <p className="mt-3 text-xs leading-relaxed text-slate-600">
                Built from standard 5000 L modules. Faster delivery, easier maintenance — one tank
                can be cleaned while others stay in service.
              </p>
            </div>
            <div className="relative rounded-lg border-2 border-[#2E75B6] bg-white p-5 shadow-md">
              <div className="absolute -top-2.5 left-4 rounded bg-[#2E75B6] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-white">
                Custom build
              </div>
              <div className="mb-1 mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Option B — Single custom tank
              </div>
              <div className="mb-3 text-lg font-semibold text-slate-900">
                {(result.custom_rounded_l ?? 0).toLocaleString('en-IN')} L bespoke
              </div>
              <SpecRow k="Configuration" v="Single bespoke tank" />
              <SpecRow
                k="Total capacity"
                v={`${(result.custom_rounded_l ?? 0).toLocaleString('en-IN')} L (rounded to nearest ${
                  (result.custom_rounded_l ?? 0) <= 10000 ? '500 L' : '1000 L'
                })`}
              />
              <SpecRow k="Inner tank grade" v={result.bom.inner_grade ?? result.bom.tank_grade} />
              <SpecRow k="Sheet thickness" v="Engineered to size" italic />
              <SpecRow k="Insulation" v="Customised" italic />
              <p className="mt-3 text-xs font-medium leading-relaxed text-[#1F4E79]">
                Sheet thickness, insulation type (PUF / Rockwool) and thickness will be designed by
                the Sunstellar engineering team based on capacity, dimensions, and site conditions.
              </p>
            </div>
          </div>
          <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-xs leading-relaxed text-slate-600">
            <strong className="text-slate-800">Note:</strong> You can customise a single tank or
            configure multiple tanks of any capacity to match your exact requirement. Please ask the
            Sunstellar team for customised tank options.
          </div>
        </div>
      )}

      {/* Indicative capex — AURA-only addition. The reference HTML omits capex
          because the customer-facing widget never showed numbers; in-AURA reps
          need it for the conversation, so we keep a single tile. */}
      {result.capex_inr > 0 && (
        <div className="flex items-baseline justify-between rounded-md border border-slate-200 bg-stone-50 px-4 py-3">
          <span className="text-sm text-slate-600">Indicative capex</span>
          <span
            className="font-mono text-lg font-semibold text-slate-900"
            data-testid="ss-tank-capex"
          >
            {formatINR(result.capex_inr, false)}
          </span>
        </div>
      )}

      {/* Tier escalation note */}
      {result.diagnostics.tier_was_escalated && (
        <div
          className="rounded-md border border-blue-300 bg-blue-50 px-3 py-2 text-sm text-blue-900"
          data-testid="ss-tank-tier-escalated"
        >
          Environment auto-escalated tier → <strong>{result.diagnostics.resolved_tier}</strong>.
        </div>
      )}
    </div>
  );
}

function SpecRow({ k, v, italic }: { k: string; v: string; italic?: boolean }) {
  return (
    <div className="flex items-center justify-between border-b border-slate-100 py-1.5 text-sm last:border-none">
      <span className="text-slate-500">{k}</span>
      <span className={`font-semibold text-slate-800 ${italic ? 'italic text-[#2E75B6]' : ''}`}>
        {v}
      </span>
    </div>
  );
}

// ─── Reference helper strings (verbatim from ss-tank.html) ──────────────────

function tierLabel(t: 't1' | 't2' | 't3'): string {
  if (t === 't1') return 'Tier 1 — Soft water (TDS up to 300 ppm)';
  if (t === 't2') return 'Tier 2 — Hard water (TDS up to 600 ppm)';
  return 'Tier 3 — Brackish water (TDS up to 1000 ppm)';
}

function recReason(t: 't1' | 't2' | 't3'): string {
  if (t === 't1')
    return 'Standard 316L spec is well above what soft municipal water demands. Cost-effective with full corrosion margin.';
  if (t === 't2')
    return 'Molybdenum content in 316L resists chlorides typical of borewell water. 1.5 mm wall handles standard-life demands.';
  return 'Duplex steel handles chlorides up to ~1000 ppm with far better pitting resistance than 316L. Required for coastal / known hard water sources.';
}

function premLabel(t: 't1' | 't2' | 't3'): string {
  if (t === 't1') return 'Sheet upgrade — 1.5 mm';
  return '100 mm insulation';
}

function premReason(t: 't1' | 't2' | 't3'): string {
  if (t === 't1')
    return '50% thicker walls — adds 5–7 years to service life. Recommended where future water quality may change or for longer payback.';
  if (t === 't2')
    return '40% better heat retention — water stays cool 6–8 hours longer in summer. Same tank, superior insulation.';
  return '40% better heat retention. Pairs duplex grade with premium thermal performance for coastal installations.';
}
